import {
  ComponentType,
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { TeamContext } from "../team";
import {
  WalkthroughEvent,
  WalkthroughEventHandler,
  walkthroughEventProperties,
} from "./walkthrough-analytics";
import { applyWalkthroughModalSuppression } from "./walkthrough-modal-suppression";
import { WalkthroughOverlay } from "./walkthrough-overlay";
import {
  documentHasTarget,
  presentWalkthroughSteps,
  WALKTHROUGH_READY_TIMEOUT_MS,
} from "./walkthrough-ready";
import { WalkthroughRegistryEntry } from "./walkthrough-registry-entry";
import {
  WalkthroughDefinition,
  WalkthroughHighlightStep,
  WalkthroughModalStep,
  WalkthroughStep,
} from "./walkthrough-types";

export type WalkthroughSignalHandler = (payload?: unknown) => void;

export interface WalkthroughContextValue {
  active: boolean;
  /** True while a page has hidden the overlay to show its own UI. */
  paused: boolean;
  currentStep: WalkthroughStep | undefined;
  currentStepIndex: number;
  totalSteps: number;
  showMockData: boolean;
  /** Mock data source loaded by the active walkthrough's registry entry. */
  mockSource: unknown;
  /** Overlay component loaded by the active walkthrough's registry entry. */
  overlayComponent: ComponentType<any> | undefined;
  next(): void;
  back(): void;
  dismiss(): void;
  /**
   * Hide the overlay and lift modal suppression without ending the
   * walkthrough, so a page can show its own dialog. `resume` restores it.
   */
  pause(): void;
  resume(): void;
  /** Imperatively start a walkthrough by id, bypassing the URL param. */
  start(walkthroughId: string): void;
  /** Fire a named signal to all subscribers. */
  emit(name: string, payload?: unknown): void;
  /** Subscribe to a named signal. Returns an unsubscribe function. */
  subscribe(name: string, handler: WalkthroughSignalHandler): () => void;
}

export const WalkthroughContext = createContext<
  WalkthroughContextValue | undefined
>(undefined);
WalkthroughContext.displayName = "WalkthroughContext";

const WALKTHROUGH_PARAM = "walkthrough";

/**
 * WalkthroughProvider sits above TeamAndUserRequired, which renders null
 * until team loads. Hold the URL param until then so highlight steps do
 * not start against an empty page.
 */
export function resolveWalkthroughName(
  teamLoaded: boolean,
  rawParam: string | null,
): string | null {
  return teamLoaded ? rawParam : null;
}

function getWalkthroughEntry(
  registry: WalkthroughRegistryEntry[],
  id: string,
): WalkthroughRegistryEntry | undefined {
  return registry.find((entry) => entry.id === id);
}

type ResolvedStep = WalkthroughModalStep | WalkthroughHighlightStep;

function isRenderable(step: WalkthroughStep): step is ResolvedStep {
  return step.type !== "signal";
}

function computeRenderableInfo(
  steps: WalkthroughStep[],
  currentStepIndex: number,
): { index: number; total: number } {
  let total = 0;
  let index = 0;
  for (const [i, step] of steps.entries()) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (!isRenderable(step!)) {
      continue;
    }
    if (i === currentStepIndex) {
      index = total;
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (!(step!.type === "modal" && step!.excludeFromStepCount)) {
      total++;
    }
  }
  return { index, total };
}

export const WalkthroughProvider = memo(function WalkthroughProvider({
  children,
  registry,
  onEvent,
}: {
  children: ReactNode;
  registry: WalkthroughRegistryEntry[];
  onEvent?: WalkthroughEventHandler;
}) {
  const team = useContext(TeamContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const walkthroughName = resolveWalkthroughName(
    team != null,
    searchParams.get(WALKTHROUGH_PARAM),
  );

  const [definition, setDefinition] = useState<
    WalkthroughDefinition | undefined
  >(undefined);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [paused, setPaused] = useState(false);
  /** False while a definition with a `readySelector` waits for its page. */
  const [ready, setReady] = useState(false);
  const [loadedWalkthroughName, setLoadedWalkthroughName] = useState<
    string | undefined
  >(undefined);
  /** Bumped on every finished load, so each run reports its own start. */
  const [loadId, setLoadId] = useState(0);
  const [mockSource, setMockSource] = useState<unknown>(undefined);
  const [overlayComponent, setOverlayComponent] = useState<
    ComponentType<any> | undefined
  >(undefined);

  const subscribersRef = useRef<Map<string, Set<WalkthroughSignalHandler>>>(
    new Map(),
  );

  const subscribe = useCallback(
    (name: string, handler: WalkthroughSignalHandler) => {
      let set = subscribersRef.current.get(name);
      if (!set) {
        set = new Set();
        subscribersRef.current.set(name, set);
      }
      set.add(handler);
      return () => {
        const current = subscribersRef.current.get(name);
        if (!current) {
          return;
        }
        current.delete(handler);
        if (current.size === 0) {
          subscribersRef.current.delete(name);
        }
      };
    },
    [],
  );

  const emit = useCallback((name: string, payload?: unknown) => {
    const set = subscribersRef.current.get(name);
    if (!set) {
      return;
    }
    for (const handler of set) {
      handler(payload);
    }
  }, []);

  const loadWalkthrough = useCallback(
    (name: string): (() => void) => {
      const entry = getWalkthroughEntry(registry, name);
      if (!entry) {
        return () => {};
      }

      let cancelled = false;

      Promise.all([
        entry.loader(),
        entry.mockSourceLoader
          ? entry.mockSourceLoader()
          : Promise.resolve(undefined),
        entry.overlayLoader
          ? entry.overlayLoader()
          : Promise.resolve(undefined),
      ])
        .then(([defModule, mockModule, overlayModule]) => {
          if (cancelled) {
            return;
          }
          setDefinition(defModule.definition);
          setMockSource(mockModule?.source);
          // Wrap setter call so React doesn't treat the component as a state
          // initializer function and invoke it.
          setOverlayComponent(() => overlayModule?.Component);
          setCurrentStepIndex(0);
          setDismissed(false);
          setPaused(false);
          setReady(!defModule.definition.readySelector);
          setLoadedWalkthroughName(name);
          setLoadId((id) => id + 1);
        })
        .catch((err) => {
          console.error("Failed to load walkthrough:", name, err);
        });

      return () => {
        cancelled = true;
      };
    },
    [registry],
  );

  useEffect(() => {
    if (!walkthroughName) {
      // Don't clear an already-loaded walkthrough when React Router
      // navigation drops the search param from the URL.
      if (!loadedWalkthroughName) {
        setDefinition(undefined);
        setCurrentStepIndex(0);
        setDismissed(false);
      }
      return;
    }

    // Skip reloading if we already have this walkthrough loaded
    if (loadedWalkthroughName === walkthroughName) {
      return;
    }

    return loadWalkthrough(walkthroughName);
  }, [walkthroughName, loadedWalkthroughName, loadWalkthrough]);

  const removeParam = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(WALKTHROUGH_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const endWalkthrough = useCallback(() => {
    setDismissed(true);
    setPaused(false);
    setLoadedWalkthroughName(undefined);
    setDefinition(undefined);
    setCurrentStepIndex(0);
    setMockSource(undefined);
    setOverlayComponent(undefined);
    removeParam();
  }, [removeParam]);

  const waitingDefinition =
    definition && !dismissed && !ready && definition.readySelector
      ? definition
      : undefined;
  useEffect(() => {
    const readySelector = waitingDefinition?.readySelector;
    if (!waitingDefinition || !readySelector) {
      return;
    }
    const reveal = (): boolean => {
      if (!documentHasTarget(readySelector)) {
        return false;
      }
      const steps = presentWalkthroughSteps(
        waitingDefinition.steps,
        documentHasTarget,
      );
      if (steps.length) {
        setDefinition({ ...waitingDefinition, steps });
        setCurrentStepIndex(0);
        setReady(true);
      } else {
        endWalkthrough();
      }
      return true;
    };
    if (reveal()) {
      return;
    }
    const timeout = setTimeout(endWalkthrough, WALKTHROUGH_READY_TIMEOUT_MS);
    const observer = new MutationObserver(() => {
      if (reveal()) {
        observer.disconnect();
        clearTimeout(timeout);
      }
    });
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
    });
    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [waitingDefinition, endWalkthrough]);

  const active = !!definition && !dismissed && ready;

  const trackEvent = useCallback(
    (event: WalkthroughEvent, stepIndex: number) => {
      if (definition) {
        onEvent?.(event, walkthroughEventProperties(definition, stepIndex));
      }
    },
    [definition, onEvent],
  );

  const dismiss = useCallback(() => {
    if (active) {
      trackEvent(WalkthroughEvent.DISMISSED, currentStepIndex);
    }
    endWalkthrough();
  }, [active, currentStepIndex, trackEvent, endWalkthrough]);

  // Read through a ref so `start` stays stable for autostart effects.
  const dismissActiveRef = useRef(dismiss);
  dismissActiveRef.current = () => {
    if (active) {
      dismiss();
    }
  };

  // Tracks the in-flight cleanup for a programmatically-started load so it can
  // be cancelled on unmount or when a new walkthrough is started.
  const startCleanupRef = useRef<(() => void) | undefined>(undefined);
  const start = useCallback(
    (walkthroughId: string) => {
      // End a tour still on screen, so it reports a dismissal and the
      // replacement reports its own start.
      dismissActiveRef.current();
      startCleanupRef.current?.();
      startCleanupRef.current = loadWalkthrough(walkthroughId);
    },
    [loadWalkthrough],
  );
  useEffect(() => () => startCleanupRef.current?.(), []);

  // Keyed on the load, not on `active` turning true: a replacement tour can
  // batch into the same render that ends the old one, so `active` never drops.
  const startedLoadIdRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!active) {
      return;
    }
    if (startedLoadIdRef.current !== loadId) {
      startedLoadIdRef.current = loadId;
      trackEvent(WalkthroughEvent.STARTED, currentStepIndex);
    }
    trackEvent(WalkthroughEvent.STEP_VIEWED, currentStepIndex);
  }, [active, loadId, currentStepIndex, trackEvent]);

  const next = useCallback(() => {
    if (!definition) {
      return;
    }
    let nextIndex = currentStepIndex + 1;

    // Walk forward, skipping:
    //  - signal steps (non-renderable; emit their forward signal as we pass)
    //  - highlight steps with skipIfMissing=true whose target isn't in the
    //    DOM right now (e.g., sidebar views that don't render on empty teams)
    while (nextIndex < definition.steps.length) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const s = definition.steps[nextIndex]!;
      if (s.type === "signal") {
        if (s.forward) {
          emit(s.forward, s.payload);
        }
        nextIndex++;
        continue;
      }
      if (s.type === "highlight" && s.skipIfMissing) {
        try {
          if (!document.querySelector(s.targetSelector)) {
            nextIndex++;
            continue;
          }
        } catch {
          nextIndex++;
          continue;
        }
      }
      break;
    }

    if (nextIndex >= definition.steps.length) {
      trackEvent(WalkthroughEvent.COMPLETED, currentStepIndex);
      endWalkthrough();
      return;
    }

    setCurrentStepIndex(nextIndex);
  }, [definition, currentStepIndex, endWalkthrough, emit, trackEvent]);

  const back = useCallback(() => {
    if (!definition) {
      return;
    }
    let prevIndex = currentStepIndex - 1;

    // Walk backwards, skipping steps we can't render:
    //  - signal steps (non-renderable; emit their backward signal as we pass)
    //  - highlight steps whose target element isn't in the DOM (e.g. inside
    //    a process modal that's no longer open)
    while (prevIndex >= 0) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const s = definition.steps[prevIndex]!;
      if (s.type === "signal") {
        if (s.backward) {
          emit(s.backward, s.payload);
        }
        prevIndex--;
        continue;
      }
      if (s.type === "highlight") {
        try {
          if (!document.querySelector(s.targetSelector)) {
            prevIndex--;
            continue;
          }
        } catch {
          prevIndex--;
          continue;
        }
      }
      break;
    }

    if (prevIndex < 0) {
      return;
    }
    setCurrentStepIndex(prevIndex);
  }, [definition, currentStepIndex, emit]);

  const currentRawStep = definition?.steps[currentStepIndex];

  const pause = useCallback(() => {
    if (active) {
      setPaused(true);
    }
  }, [active]);
  const resume = useCallback(() => setPaused(false), []);

  useEffect(
    () => applyWalkthroughModalSuppression(active && !paused),
    [active, paused],
  );

  const currentStep = active && currentRawStep ? currentRawStep : undefined;

  // Whether the active step's section requested mock data injection
  const showMockData = useMemo(() => {
    if (!active) {
      return false;
    }
    return !!currentStep?.section?.showMockData;
  }, [active, currentStep]);

  const renderableInfo = useMemo(
    () =>
      definition
        ? computeRenderableInfo(definition.steps, currentStepIndex)
        : { index: 0, total: 0 },
    [definition, currentStepIndex],
  );

  const resolved =
    currentStep && isRenderable(currentStep) ? currentStep : undefined;

  const value = useMemo<WalkthroughContextValue>(
    () => ({
      active,
      paused,
      currentStep,
      currentStepIndex,
      totalSteps: renderableInfo.total,
      showMockData,
      mockSource,
      overlayComponent,
      next,
      back,
      dismiss,
      pause,
      resume,
      start,
      emit,
      subscribe,
    }),
    [
      active,
      paused,
      currentStep,
      currentStepIndex,
      renderableInfo.total,
      showMockData,
      mockSource,
      overlayComponent,
      next,
      back,
      dismiss,
      pause,
      resume,
      start,
      emit,
      subscribe,
    ],
  );

  return (
    <WalkthroughContext.Provider value={value}>
      {children}
      {active &&
        !paused &&
        resolved &&
        createPortal(
          <WalkthroughOverlay
            back={back}
            dismiss={dismiss}
            next={next}
            step={resolved}
            stepIndex={renderableInfo.index}
            totalSteps={renderableInfo.total}
          />,
          document.body,
        )}
    </WalkthroughContext.Provider>
  );
});
